import { useRef, useEffect, useState } from "react";
import { FullscreenModal, Toast } from "shared";
import { TOAST_TYPES_ENUM } from "enums";
import { useUpdateInvestment } from "hooks/investments";
import { useGetCountriesCurrency } from "hooks/countries";
import { InvestmentItem } from "../../types";
import { useEditFileOperations } from "./utils/fileOperations";
import {
  TreasuryBondsForm,
  CryptoForm,
  CurrenciesForm,
  InvestmentPolicyForm,
  LoansForm,
  RealEstateForm,
  OtherAssetForm,
  PreciousStonesForm,
  ArtworkForm,
  PreciousMetalsForm,
  TreasuryBondsValues,
  CryptoValues,
  CurrenciesValues,
  InvestmentPolicyValues,
  LoansValues,
  RealEstateValues,
  OtherAssetValues,
  PreciousStonesValues,
  ArtworkValues,
  PreciousMetalsValues,
} from "./forms";

interface EditInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: InvestmentItem | null;
  slug: string;
}

const MODAL_TITLES: Record<string, string> = {
  treasury_bonds: "Edycja obligacji skarbowych zakupionych poza Reliance",
  currencies: "Edycja walut zakupionych poza Reliance",
  crypto: "Edycja tokenów / kryptowalut zakupionych poza Reliance",
  investment_policy:
    "Edycja polisy inwestycyjnej / IKE zakupionej poza Reliance",
  artwork: "Edycja dzieła sztuki zakupionego poza Reliance",
  precious_stones: "Edycja kamienia szlachetnego",
  precious_metals: "Edycja metali szlachetnych zakupionych poza Reliance",
  real_estate: "Edycja nieruchomości",
  loans: "Edycja pożyczki",
};

const resolveDurationDate = (
  durationDate: string,
  durationDateValue: string,
): string | null => {
  if (durationDate === "indefinite") return null;
  if (durationDate === "specific_date") return durationDateValue || "";
  return "2000-01-01";
};

const resolveAutoRenewal = (val: string): boolean | null => {
  if (val === "true") return true;
  if (val === "false") return false;
  return null;
};

const getNewLocalFiles = (files: File[] | null | undefined): File[] =>
  Array.isArray(files) ? files.filter((f) => f instanceof File) : [];

const EditInvestmentModal = ({
  isOpen,
  onClose,
  item,
  slug,
}: EditInvestmentModalProps) => {
  const { mutate: updateInvestment, isPending } = useUpdateInvestment({
    onError: (error) => {
      if ((error as any).status === 404) onClose();
    },
  });
  const [isUploading, setIsUploading] = useState(false);
  const { data: currenciesData } = useGetCountriesCurrency();
  const currencyOptions = currenciesData ?? [];
  const { processContractFiles, processPhotoFiles } = useEditFileOperations();

  const removedPhotoUrls = useRef<Set<string>>(new Set());
  const removedContractUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    removedPhotoUrls.current = new Set();
    removedContractUrls.current = new Set();
  }, [item?.uuid]);

  const handleFileOperations = async (
    investmentUuid: string,
    existingContractFiles: any[],
    newLocalContracts: File[],
    existingPhotoFiles?: any[],
    newLocalPhotos?: File[],
    contractRole?: "contract" | "closure_contract",
  ) => {
    setIsUploading(true);
    try {
      await processContractFiles({
        investmentUuid,
        existingFiles: existingContractFiles,
        removedUrls: removedContractUrls.current,
        newLocalFiles: newLocalContracts,
        role: contractRole,
      });
      if (existingPhotoFiles && newLocalPhotos) {
        await processPhotoFiles({
          investmentUuid,
          existingFiles: existingPhotoFiles,
          removedUrls: removedPhotoUrls.current,
          newLocalFiles: newLocalPhotos,
        });
      }
    } finally {
      setIsUploading(false);
      Toast({
        message: "Aktywo zaktualizowane.",
        type: TOAST_TYPES_ENUM.SUCCESS,
        id: "update-investment",
      });
      onClose();
    }
  };

  const handleTreasurySubmit = (values: TreasuryBondsValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        quantity: values.quantity !== "" ? Number(values.quantity) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          interestRate:
            values.interestRate !== "" ? Number(values.interestRate) : null,
          interestRateType: values.interestType || null,
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
          ),
      },
    );
  };

  const handleCryptoSubmit = (values: CryptoValues) => {
    if (!item) return;
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      },
      {
        onSuccess: () => {
          Toast({
            message: "Aktywo zaktualizowane.",
            type: TOAST_TYPES_ENUM.SUCCESS,
            id: "update-investment",
          });
          onClose();
        },
      },
    );
  };

  const handleCurrenciesSubmit = (values: CurrenciesValues) => {
    if (!item) return;
    updateInvestment(
      {
        uuid: item.uuid,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      },
      {
        onSuccess: () => {
          Toast({
            message: "Aktywo zaktualizowane.",
            type: TOAST_TYPES_ENUM.SUCCESS,
            id: "update-investment",
          });
          onClose();
        },
      },
    );
  };

  const handleInvestmentPolicySubmit = (values: InvestmentPolicyValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          currencyUuid: values.currencyUuid,
          contractDate: values.contractDate || null,
          durationDate: resolveDurationDate(
            values.durationDate,
            values.durationDateValue,
          ),
          premiumPayment: values.premiumPayment || null,
        },
      },
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
          ),
      },
    );
  };

  const handleLoansSubmit = (values: LoansValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          loanType: values.loanType,
          entity: values.entity,
          interestRate:
            values.interestRate !== "" ? Number(values.interestRate) : null,
          interestRateType: values.interestRateType || null,
          interestPayment: values.interestPayment || null,
          contractDate: values.contractDate || null,
          durationDate: resolveDurationDate(
            values.durationDate,
            values.durationDateValue,
          ),
          autoRenewal: resolveAutoRenewal(values.autoRenewal),
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
          ),
      },
    );
  };

  const handleRealEstateSubmit = (values: RealEstateValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const existingPhotos = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          propertyType: values.propertyType,
          propertyTypeOther:
            values.propertyType === "other"
              ? values.propertyTypeOther || null
              : null,
          area: values.area !== "" ? Number(values.area) : null,
          city: values.city || null,
          address: values.address || null,
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
            existingPhotos,
            getNewLocalFiles(values.photoFiles as any),
          ),
      },
    );
  };

  const handleArtworkSubmit = (values: ArtworkValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const existingPhotos = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          artworkType: values.artworkType,
          author: values.author || null,
          eraAge: values.eraAge || null,
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
            existingPhotos,
            getNewLocalFiles(values.photoFiles as any),
          ),
      },
    );
  };

  const handlePreciousStonesSubmit = (values: PreciousStonesValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          weight: values.weight !== "" ? Number(values.weight) : null,
          cut: values.cut || null,
          color: values.color || null,
          clarity: values.clarity || null,
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
          ),
      },
    );
  };

  const handlePreciousMetalsSubmit = (values: PreciousMetalsValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract",
    );
    const resolvedName =
      values.propertyType === "gold"
        ? "Złoto"
        : values.propertyType === "silver"
        ? "Srebro"
        : values.name || null;
    updateInvestment(
      {
        uuid: item.uuid,
        name: resolvedName,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
        details: {
          propertyType: values.propertyType,
          weight: values.weight !== "" ? Number(values.weight) : null,
          finess: values.finess || null,
        },
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
          ),
      },
    );
  };

  const handleOtherAssetSubmit = (values: OtherAssetValues) => {
    if (!item) return;
    const existingContracts = (item.files ?? []).filter(
      (f: any) => f.role === "contract" || f.role === "attachment",
    );
    const existingPhotos = (item.files ?? []).filter(
      (f: any) => f.role === "photo",
    );
    updateInvestment(
      {
        uuid: item.uuid,
        name: values.name || null,
        primaryValue:
          values.primaryValue !== "" ? Number(values.primaryValue) : undefined,
        currencyUuid: values.currencyUuid || undefined,
        additionalInfo: values.additionalInfo || null,
        relianceCanView: Boolean(values.relianceCanView),
      } as any,
      {
        onSuccess: () =>
          handleFileOperations(
            item.uuid,
            existingContracts,
            getNewLocalFiles(values.contractFile),
            existingPhotos,
            getNewLocalFiles(values.photoFiles as any),
          ),
      },
    );
  };

  const knownSlugs = [
    "treasury_bonds",
    "crypto",
    "currencies",
    "investment_policy",
    "artwork",
    "precious_stones",
    "precious_metals",
    "loans",
    "real_estate",
  ];

  const title = MODAL_TITLES[slug] ?? "Edycja aktywa";

  const onRemovedContract = (f: { url: string }) =>
    removedContractUrls.current.add(f.url);
  const onRemovedPhoto = (f: { url: string }) =>
    removedPhotoUrls.current.add(f.url);

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <>
        <h2 className="fullscreen-modal__header">{title}</h2>

        {item && slug === "treasury_bonds" && (
          <TreasuryBondsForm
            item={item}
            onSubmit={handleTreasurySubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "crypto" && (
          <CryptoForm
            item={item}
            onSubmit={handleCryptoSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
          />
        )}
        {item && slug === "currencies" && (
          <CurrenciesForm
            item={item}
            onSubmit={handleCurrenciesSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
          />
        )}
        {item && slug === "investment_policy" && (
          <InvestmentPolicyForm
            item={item}
            onSubmit={handleInvestmentPolicySubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "artwork" && (
          <ArtworkForm
            item={item}
            onSubmit={handleArtworkSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedPhoto={onRemovedPhoto}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "precious_stones" && (
          <PreciousStonesForm
            item={item}
            onSubmit={handlePreciousStonesSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "precious_metals" && (
          <PreciousMetalsForm
            item={item}
            onSubmit={handlePreciousMetalsSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "loans" && (
          <LoansForm
            item={item}
            onSubmit={handleLoansSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && slug === "real_estate" && (
          <RealEstateForm
            item={item}
            onSubmit={handleRealEstateSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedPhoto={onRemovedPhoto}
            onRemovedContract={onRemovedContract}
          />
        )}
        {item && !knownSlugs.includes(slug) && (
          <OtherAssetForm
            item={item}
            onSubmit={handleOtherAssetSubmit}
            onCancel={onClose}
            isPending={isPending || isUploading}
            currencyOptions={currencyOptions}
            onRemovedPhoto={onRemovedPhoto}
            onRemovedContract={onRemovedContract}
          />
        )}
      </>
    </FullscreenModal>
  );
};

export default EditInvestmentModal;
